import { describeArcSegment, polarToCartesian } from '../../utils/describe-arc-segment';
import { getRolePlayGradientStops } from '../../utils/get-role-play-gradient-stops';
import { getScoreColorScale } from '../../utils/get-score-color-scale';
import { getStopColorAt } from '../../utils/get-color-stop-at';
import { RoleplayGradientArcProps } from '../../types';
import { useMemo } from 'react';

// ================================
// How the gradient arc works
// ================================
//
// react-circular-progressbar renders a single solid-color SVG arc. To achieve a
// multi-color gradient that transitions through score bands, we overlay a custom
// SVG on top of the library arc and replace it with a series of short path
// segments, each painted a slightly different color sampled from a gradient stop
// array. The result looks like a smooth color gradient that tracks the animated
// progress value.
//
// Rendering pipeline:
//   1. progressRatio  — clamp progressValue / valueMax to [0, 1]
//   2. gradient stops — derive color band stops from scoreValue via getRolePlayGradientStops
//   3. segments       — divide the visible arc into N short segments, each with its
//                       own sampled color from the stop array
//   4. end-caps       — two filled circles cover the blunt stroke ends at the very
//                       start and end of the arc so the termination looks rounded

/**
 * Renders a segmented gradient arc for role-play mode, with segments colored
 * based on score bands. The arc is divided into segments that transition
 * through the defined score colors according to the current score value.
 * @author Copilot
 */
export const RoleplayGradientArc: React.FC<RoleplayGradientArcProps> = ({
  circleRatio,
  flatEndCap = false,
  isLoading = false,
  progressValue,
  runtimeRatingCutoffs,
  rotation,
  scoreValue,
  strokeWidth,
  valueMax,
}) => {
  const effectiveProgressValue = isLoading ? 100 : progressValue;
  const effectiveScoreValue = isLoading ? 100 : scoreValue;

  // Normalise to a 0–1 ratio, guarding against a zero valueMax to avoid division by zero.
  const progressRatio = Math.max(
    0,
    Math.min(1, valueMax > 0 ? effectiveProgressValue / valueMax : 0),
  );

  // Build the array of colored path segments that form the visible arc.
  // Memoised so segments are only recalculated when a relevant prop changes.
  const segments = useMemo(() => {
    // Nothing to render until the animation starts.
    if (progressRatio <= 0) return [];

    // Derive gradient stops for the current score value, based on the active color scale.
    // scoreValue drives which color band boundaries are used; progressValue drives how
    // far along the arc is drawn — these can differ during animation.
    const stops = getRolePlayGradientStops(
      effectiveScoreValue,
      getScoreColorScale(),
      runtimeRatingCutoffs,
    );

    // Full arc length in degrees if the circle were complete.
    const totalArcDegrees = 360 * circleRatio;

    // Degrees actually drawn based on how far progress has advanced.
    const visibleArcDegrees = totalArcDegrees * progressRatio;

    // Match react-circular-progressbar orientation:
    // The library starts at the top of the circle (−90°) offset by the rotation prop.
    const startAngle = rotation * 360 - 90;

    // Use at least 24 segments so short arcs still look smooth; for longer arcs
    // use roughly one segment per 2°, which keeps colour transitions imperceptible.
    const segmentCount = Math.max(24, Math.ceil(visibleArcDegrees / 2));

    // The SVG viewBox is 100×100, with the arc centred at (50, 50).
    // strokeWidth is subtracted so the path follows the centre-line of the stroke.
    const radius = 50 - strokeWidth / 2;
    const cx = 50;
    const cy = 50;

    // Generate an array of segments, each with a path definition and a color
    // sampled from the gradient stop array at the segment's midpoint. The
    // describeArcSegment utility converts the segment's start and end angles
    // into an SVG path string.
    return Array.from({ length: segmentCount }, (_, i) => {
      // Normalised position of this segment's leading and trailing edges (0–1).
      const t0 = i / segmentCount;
      const t1 = (i + 1) / segmentCount;

      // Sample color at the midpoint of the segment to avoid visible banding at edges.
      const mid = (t0 + t1) / 2;

      // Convert normalised positions to absolute angles for the SVG arc command.
      const segmentStart = startAngle + visibleArcDegrees * t0;
      const segmentEnd = startAngle + visibleArcDegrees * t1;

      return {
        // Interpolated color from the gradient stop array at this position.
        color: getStopColorAt(stops, mid),
        // SVG path data string: a short arc from segmentStart to segmentEnd.
        d: describeArcSegment(cx, cy, radius, segmentStart, segmentEnd),
      };
    });
  }, [
    circleRatio,
    effectiveScoreValue,
    progressRatio,
    rotation,
    runtimeRatingCutoffs,
    strokeWidth,
  ]);

  // Return nothing when there are no segments to draw (e.g. progress is 0).
  if (segments.length === 0) return null;

  // ================================
  // End-cap geometry
  // ================================
  // SVG strokes with strokeLinecap="butt" leave flat ends. Two small filled
  // circles placed exactly at the start and end of the arc simulate rounded caps
  // without using strokeLinecap="round", which would alter all segment joints.

  const radius = 50 - strokeWidth / 2;
  const startAngle = rotation * 360 - 90;

  // Cartesian position of the very beginning of the arc.
  const start = polarToCartesian(50, 50, radius, startAngle);

  // Cartesian position of the arc's current endpoint, determined by how far
  // progress has advanced across the total arc span.
  const end = polarToCartesian(50, 50, radius, startAngle + 360 * circleRatio * progressRatio);

  // The start cap uses the first segment's color; the end cap uses the last.
  const firstColor = segments[0].color;
  const lastColor = segments[segments.length - 1].color;

  // classNames
  const classNames = [
    // Ensure the arc doesn't accept mouse events
    'pointer-events-none',
    // Position the SVG to perfectly overlay the progress circle.
    'absolute inset-0 h-full w-full',
    // Pulse animation when loading
    isLoading ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : '',
  ].join(' ');

  // =============================================
  // Render the SVG arc with segments and end caps
  // =============================================
  return (
    // The SVG is positioned absolutely over the CircularProgressbar, matching
    // its dimensions exactly. pointer-events-none ensures it doesn't block
    // interaction with underlying elements. aria-hidden hides it from assistive
    // technology since the score value is already conveyed by the text label.
    <svg
      aria-hidden="true"
      className={classNames}
      preserveAspectRatio="xMidYMid meet"
      viewBox="0 0 100 100"
    >
      {/* Gradient arc segments — each segment is an independent SVG arc path */}
      {segments.map((segment, index) => (
        <path
          d={segment.d}
          fill="none"
          key={`${index}-${segment.color}`}
          stroke={segment.color}
          strokeLinecap="butt"
          strokeWidth={strokeWidth}
        />
      ))}

      {/* Rounded end-cap at the start of the arc */}
      <circle cx={start.x} cy={start.y} fill={firstColor} r={strokeWidth / 2} />

      {/* Rounded end-cap at the current end of the arc — omitted when flatEndCap is
          true so the arc terminates flat at a band boundary (25/50/75). */}
      {(!flatEndCap || isLoading) && (
        <circle cx={end.x} cy={end.y} fill={lastColor} r={strokeWidth / 2} />
      )}
    </svg>
  );
};

RoleplayGradientArc.displayName = 'RoleplayGradientArc';

export default RoleplayGradientArc;
